import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';

/** A breadcrumb hop under the WAAP console base. Omit `slug` for the current page. */
export interface WaapCrumb {
  label: ReactNode;
  /** Path segment under `/services/waap/:serviceId`; omit for the current crumb. */
  slug?: string;
}

const ROOT_LABEL = 'Web Application & API Protection';

/**
 * Consistent breadcrumb trail for every WAAP console page, shown in the
 * PageHeader above the title. Always rooted at the WAAP dashboard; pass sub-page
 * hops via `trail`. The dashboard itself passes no trail (root = current page).
 */
export function WaapConsoleBreadcrumbs({ trail = [] }: { trail?: WaapCrumb[] }) {
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/waap/${serviceId}`;

  const items = [
    trail.length === 0 ? (
      <BreadcrumbItem key="root" current>
        {ROOT_LABEL}
      </BreadcrumbItem>
    ) : (
      <BreadcrumbItem key="root" asChild>
        <Link to={base}>{ROOT_LABEL}</Link>
      </BreadcrumbItem>
    ),
    ...trail.map((crumb, i) =>
      crumb.slug ? (
        <BreadcrumbItem key={i} asChild>
          <Link to={`${base}/${crumb.slug}`}>{crumb.label}</Link>
        </BreadcrumbItem>
      ) : (
        <BreadcrumbItem key={i} current>
          {crumb.label}
        </BreadcrumbItem>
      ),
    ),
  ];

  return <Breadcrumbs>{items}</Breadcrumbs>;
}
